import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { SurfaceCard } from "@/components/common/Cards";
import { StatusBadge } from "@/components/common/StatusBadge";
import { formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { ClipboardCheck, Plus, ChevronRight, Paperclip } from "lucide-react";
import { fileUrl } from "@/components/common/FileGallery";

export default function TreatmentPlans() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  useEffect(() => { api.get("/treatment-plans").then((r) => setPlans(r.data)).catch(() => setPlans([])); }, []);

  return (
    <div>
      <PageHeader title="Treatment Plans" subtitle="AI-drafted, doctor-approved implant treatment plans" breadcrumb={["Clinical", "Treatment Plans"]}
        actions={<Button onClick={() => navigate("/consultation/new")} data-testid="tp-new-consult" className="bg-[#00A85A] hover:bg-[#008F4D] text-white rounded-lg"><Plus className="h-4 w-4 mr-1.5" />New Consultation</Button>} />
      {plans && plans.length === 0 ? (
        <EmptyState icon={ClipboardCheck} title="No treatment plans yet" description="Start a consultation and generate a treatment plan to see it here." actionLabel="New Consultation" onAction={() => navigate("/consultation/new")} testId="tp-empty" />
      ) : (
        <div className="space-y-3">
          {(plans || []).map((p) => (
            <SurfaceCard key={p.id} className="p-4 cursor-pointer hover:border-[#00A85A]/40" data-testid={`tp-card-${p.id}`} onClick={() => navigate(`/treatment-plan/${p.id}`)}>
              <div className="flex items-center gap-4">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#E8F7F0] dark:bg-[#00A85A]/15"><ClipboardCheck className="h-5 w-5 text-[#00A85A]" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2"><div className="text-sm font-bold text-[#101516] dark:text-white truncate">{p.patient_name} — Treatment Plan</div><span className="text-[10px] font-bold text-[#8B9698]">{p.version}</span></div>
                  <div className="text-xs text-[#8B9698]">{(p.content?.diagnosis?.primary || "Diagnosis pending").slice(0, 70)} · {formatDate(p.created_at)}</div>
                  {p.files?.length > 0 && <div className="mt-2 flex gap-1.5">{p.files.filter((f) => (f.content_type || "").startsWith("image/")).map((f) => <img key={f.id} src={fileUrl(f.storage_path)} alt={f.original_filename} className="h-10 w-14 rounded-md border border-[#E2E8F0] object-cover" loading="lazy" data-testid={`tp-thumb-${f.id}`} />)}</div>}
                </div>
                <span className="flex items-center gap-1 rounded-full bg-[#E8F7F0] px-2 py-0.5 text-xs font-bold text-[#008F4D]" data-testid={`tp-file-count-${p.id}`}><Paperclip className="h-3 w-3" />{p.file_count || 0}</span>
                {p.decision && <StatusBadge status={p.decision === "accepted" ? "success" : p.decision === "declined" ? "error" : "warning"} label={p.crm_state} />}
                <StatusBadge status={p.status === "approved" ? "approved" : "review_required"} />
                <ChevronRight className="h-4 w-4 text-[#8B9698]" />
              </div>
            </SurfaceCard>
          ))}
        </div>
      )}
    </div>
  );
}
